import React from 'react';
import PropTypes from 'prop-types';
import { alpha, Box, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { C, FONT_NUM } from './tokens';
import {
  distToResistanceTone,
  fmtNum,
  fmtPct,
  fmtPrice,
  fmtValue,
  getLocalizedText,
  pickScoreColor,
  prettySector,
  signalStyle,
  tierStyle,
} from './formatters';
import { Tag } from './atoms';
import { candidateRowShape, tierTagShape } from './shapes';
import AIConfidenceBadge from '../AIConfidenceBadge';

// Trailing desktop-only metric cells (distance, U/D, accumulation, liquidity, strength).
const CandidateRowDesktopTailCells = ({ row, tier, language, locale }) => (
  <>
    <TableCell
      align="right"
      sx={{
        fontFamily: FONT_NUM,
        fontWeight: 700,
        color: distToResistanceTone(row.price?.dist_from_resistance).color,
      }}
    >
      {fmtPct(row.price?.dist_from_resistance, 2)}
    </TableCell>
    <TableCell
      align="right"
      sx={{
        fontFamily: FONT_NUM,
        fontWeight: 700,
        color: (row.smart_money?.up_value_ratio ?? 0) >= 1.2 ? C.green : C.inkMid,
      }}
    >
      {fmtNum(row.smart_money?.up_value_ratio, 2)}x
    </TableCell>
    <TableCell align="right" sx={{ fontFamily: FONT_NUM, color: C.inkMid }}>
      {row.smart_money?.accumulation_days_10 ?? 0}/10
    </TableCell>
    <TableCell align="right" sx={{ fontFamily: FONT_NUM, fontWeight: 700, color: C.ink }}>
      {fmtValue(row.liquidity?.avg_value_20d, locale)}
    </TableCell>
    <TableCell
      align="right"
      sx={{ fontFamily: FONT_NUM, fontWeight: 700, color: tier ? tier.fg : C.ink }}
    >
      {fmtNum(row.strength?.strength_score, 1)}
    </TableCell>
    <TableCell align="right" sx={{ fontFamily: FONT_NUM, color: C.inkMid }}>
      {fmtNum(row.strength?.relative_strength_score, 1)}
    </TableCell>
    <TableCell sx={{ minWidth: 84 }}>
      {tier ? (
        <Tag bg={tier.bg} fg={tier.fg}>
          {getLocalizedText(row.strength?.strength_tier_label, language)}
        </Tag>
      ) : (
        <Typography sx={{ color: C.mutedSoft }}>—</Typography>
      )}
    </TableCell>
  </>
);

CandidateRowDesktopTailCells.propTypes = {
  row: candidateRowShape.isRequired,
  tier: tierTagShape,
  language: PropTypes.string,
  locale: PropTypes.string,
};

CandidateRowDesktopTailCells.defaultProps = {
  tier: null,
  language: 'en',
  locale: 'en-US',
};

// A single table row representing one breakout candidate.
const CandidateRow = ({ row, index, onOpenDetail, t, language, locale, isCompact }) => {
  const sigSt = signalStyle(row.signal);
  const tier = tierStyle(row.strength?.strength_tier);
  const companyName = row.company_name || null;
  const sectorName = prettySector(row.sector, language);
  const scoreColor = pickScoreColor(row.breakout_score);

  return (
    <TableRow
      hover
      onClick={() => onOpenDetail(row)}
      sx={{
        cursor: 'pointer',
        bgcolor: index % 2 === 0 ? 'transparent' : '#fafbfd',
        '&:hover': { bgcolor: alpha(C.accent, 0.03) },
        '& td': {
          borderBottom: '1px solid #f1f5f9',
          py: 1,
          px: 1.5,
          fontSize: '0.77rem',
          verticalAlign: 'top',
        },
        transition: 'background-color .12s',
      }}
    >
      <TableCell align="center" sx={{ width: 42 }}>
        <Box sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: FONT_NUM,
          fontSize: '0.68rem',
          fontWeight: 700,
          bgcolor: index < 3 ? alpha(scoreColor, 0.1) : '#f1f5f9',
          color: index < 3 ? scoreColor : '#94a3b8',
        }}>
          {index + 1}
        </Box>
      </TableCell>

      <TableCell sx={{ minWidth: isCompact ? 140 : 160, width: isCompact ? '100%' : 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, minWidth: 0 }}>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <Typography sx={{ fontWeight: 800, color: C.accent, fontSize: '0.82rem' }}>
              {row.ticker}
            </Typography>
          </Stack>
          {companyName ? (
            <Typography sx={{ fontSize: '0.64rem', fontWeight: 600, color: C.mutedSoft, lineHeight: 1.25, whiteSpace: 'normal', wordBreak: 'break-word' }}>
              {companyName}
            </Typography>
          ) : null}
          {isCompact && sectorName !== '—' ? (
            <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: C.inkMid, lineHeight: 1.25, whiteSpace: 'normal', wordBreak: 'break-word' }}>
              {sectorName}
            </Typography>
          ) : null}
        </Box>
      </TableCell>

      {isCompact ? null : (
        <TableCell sx={{ minWidth: 130, color: C.inkMid }}>{sectorName}</TableCell>
      )}

      <TableCell align="right" sx={{ fontFamily: FONT_NUM, fontWeight: 800, fontSize: '0.82rem', color: C.ink }}>
        {fmtNum(row.breakout_score, 1)}
      </TableCell>

      <TableCell sx={{ minWidth: 100 }}>
        <Tag bg={sigSt.bg} fg={sigSt.fg} border={sigSt.border}>
          {getLocalizedText(row.signal_label, language)}
        </Tag>
      </TableCell>

      <TableCell sx={{ minWidth: 96 }}>
        <AIConfidenceBadge
          confidence={row.ai_confidence}
          language={language}
          tooltipTitle={t('breakoutCandidates.table.aiConfidenceTooltip', 'AI confidence — pattern reliability win probability')}
          compact
        />
      </TableCell>

      <TableCell align="right" sx={{ fontFamily: FONT_NUM, fontWeight: 700, color: C.ink }}>
        {fmtPrice(row.price?.close, locale)}
      </TableCell>

      {isCompact ? null : (
        <CandidateRowDesktopTailCells
          row={row}
          tier={tier}
          language={language}
          locale={locale}
        />
      )}
    </TableRow>
  );
};

CandidateRow.propTypes = {
  row: candidateRowShape.isRequired,
  index: PropTypes.number.isRequired,
  onOpenDetail: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  language: PropTypes.string,
  locale: PropTypes.string,
  isCompact: PropTypes.bool,
};

CandidateRow.defaultProps = {
  language: 'en',
  locale: 'en-US',
  isCompact: false,
};

export default CandidateRow;
